import { css, CSSObject, SerializedStyles } from '@emotion/react';
import React from 'react';
import {
    createResponsiveStyles,
    getSizeValue,
    HoneycombTheme,
    mergeResponsiveStyleValues,
    ResponsiveStyleValue,
    useHoneycombTheme,
} from '../../../theming';

export const STATIC_STYLES: SerializedStyles = css`
    position: relative;

    // The root element is focusable to support keyboard navigation
    &:focus-visible {
        outline: 2px solid var(--hc-color-action-focus);
    }

    [data-carousel-viewport] {
        height: 100%;
        overflow: hidden;

        &:where([data-overflow='visible']) {
            overflow: visible;
        }
    }

    [data-carousel-container] {
        height: 100%;
        display: grid;
        grid-auto-flow: column;
        grid-column-gap: var(--carousel-slide-spacing);

        // fallback if visibleItems is 'auto'
        grid-auto-columns: auto;

        // Adjust grid-auto-columns to account for gaps between slides,
        // subtracting partial gap for fractional visible slides.
        grid-auto-columns: calc(
            (100% - (var(--carousel-slide-spacing) * (var(--carousel-visible-slides) - var(--carousel-gap-adjustment)))) /
                var(--carousel-visible-slides)
        );

        &:where([data-orientation='vertical']) {
            grid-auto-flow: row;
        }
    }

    [data-carousel-slide] {
        position: relative;
        transform: translate3d(0, 0, 0);

        min-width: 0;
        user-select: none;
    }

    [data-carousel-arrow-button] {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);

        /* 1. Hidden by default */
        opacity: 0;
    }

    /* 2. Disabled button behavior */
    [data-carousel-arrow-button][disabled] {
        pointer-events: auto; /* Allow blocking clicks */
    }

    /* 3. Parent hover: near-full opacity */
    &:hover [data-carousel-arrow-button]:not([disabled]) {
        opacity: 0.9;
    }

    /* 4. Button hover: full opacity */
    [data-carousel-arrow-button]:not([disabled]):hover {
        opacity: 1;
    }

    [data-carousel-arrow-button='prev'] {
        inset-inline-start: calc(var(--hc-spacing-unit) * 4);
    }

    [data-carousel-arrow-button='next'] {
        inset-inline-end: calc(var(--hc-spacing-unit) * 4);
    }
`;

function getResponsiveStyles(theme: HoneycombTheme) {
    return ({
        slideSpacing,
        visibleSlides,
        height,
    }: {
        slideSpacing: number;
        visibleSlides: number | 'auto';
        height?: string | number;
    }) => {
        const spacingValue = theme.fn.getSpacingValue(slideSpacing);

        let gapAdjustment = 1;

        if (typeof visibleSlides === 'number') {
            const fractionalPart = visibleSlides % 1;
            gapAdjustment = fractionalPart > 0 ? fractionalPart : 1;
        }

        let heightValue;
        if (typeof height === 'string' || typeof height === 'number') {
            heightValue = getSizeValue(height);
        }

        return {
            '--carousel-height': heightValue,
            '--carousel-slide-spacing': spacingValue,
            '--carousel-visible-slides': visibleSlides,
            '--carousel-gap-adjustment': gapAdjustment,
        };
    };
}

export function useDynamicStyles(
    slideSpacing: ResponsiveStyleValue<number>,
    visibleSlides: ResponsiveStyleValue<number | 'auto'>,
    height?: ResponsiveStyleValue<string | number>
): CSSObject {
    const { theme } = useHoneycombTheme();

    const styles = React.useMemo(() => {
        return {
            ...createResponsiveStyles(
                [
                    {
                        value: mergeResponsiveStyleValues({ slideSpacing, visibleSlides, height }, theme),
                        getStyles: getResponsiveStyles(theme),
                    },
                ],
                theme
            ),
        };
    }, [slideSpacing, visibleSlides, height, theme]);

    return styles;
}
